<template>
  <template v-for="(item, f) in formData" :key="f">
    <component :is="item.borderless ? AnonymousFieldset : Fieldset" :legend="item.fieldset"
               :toggleable="item.toggleable" class="input-panel">
      <div class="input-fieldset">
        <template v-for="(input, i) in item.children" :key="i">
          <div v-if="!input.bindable" class="input-element" :class="input.type">
            <label :for="input.id" v-if="!(input.type === 'image' && input.model)">{{ input.label }}</label>
            <Skeleton v-if="loading" height="4rem"></Skeleton>
            <template v-else>
              <div class="input-component" :class="input.type">
                <Select v-if="input.type === 'select'" :id="input.id" :options="input.children"
                          :placeholder="input.placeholder" v-model="input.model" optionValue="value"
                          @update:modelValue="change(input.id, input.model)" optionLabel="label" class="w-100">
                  <template #option="slotProps">
                    <div class="flex align-items-center">
                      <template v-if="input.render_as==='flag'">
                        <lang-flag :iso="slotProps.option.value.split('_')[0]" :squared="false"/>
                        &nbsp;
                      </template>
                      <span>{{ slotProps.option.label }}</span>
                    </div>
                  </template>
                </Select>
                <InputNumber v-if="input.type === 'price'"
                             :currency="item.children.find((el) => el.id === input.currency_column).model ?? 'EUR'"
                             mode="currency" :placeholder="input.placeholder" v-model="input.model"
                             @update:modelValue="change(input.id, input.model)" :id="input.id"
                             class="w-100"></InputNumber>
                <InputText v-if="input.type === 'text'" :placeholder="input.placeholder" v-model="input.model"
                           @update:modelValue="change(input.id, input.model)" :id="input.id" class="w-100"></InputText>
                <Password v-if="input.type === 'password'" :placeholder="input.placeholder" v-model="input.model"
                          @update:modelValue="change(input.id, input.model)" toggleMask :id="input.id"
                          class=""></Password>
                <SelectButton v-if="input.type === 'switch'" :id="input.id" v-model="input.model"
                              @update:modelValue="change(input.id, input.model)" optionLabel="label" optionValue="value"
                              :options="input.children" aria-labelledby="basic">
                  <template #option="slotProps">
                    <i v-if="slotProps.option.icon" :class="slotProps.option.icon"></i>
                  </template>
                </SelectButton>
                <InputNumber v-if="input.type === 'number'" v-model="input.model" showButtons :max="input.max"
                             :min="input.min" :step="1"
                             @update:modelValue="change(input.id, input.model)"></InputNumber>
                <Slider v-if="input.type === 'slider'" v-model="input.model" showButtons :max="input.max"
                        :min="input.min" :step="1" @update:modelValue="change(input.id, input.model)"
                        style="margin-top: 16px;"></Slider>
                <FileUpload v-if="input.type === 'fileUpload'" :chooseLabel="$t('common.upload')" mode="basic" name="demo[]"
                            url="/api/upload" accept="image/*" :maxFileSize="1000000"/>
                <Editor v-if="input.type === 'editor'" v-model="input.model" :modelValue="input.model"
                        @update:modelValue="change(input.id, input.model)" editorStyle="height: 320px"></Editor>
                <template v-if="input.type === 'image'">
                  <FileUpload v-if="!input.model" :name="input.id"
                              :url="require('../../../../config').SERVER_URI + 'app/upload/' + input.id + '/' + modelId"
                              :multiple="false" accept="image/*"
                              :upload-label="$t('common.upload')"
                              :choose-label="$t('common.choose')"
                              :cancel-label="$t('common.cancel')"
                              v-model="input.model"
                              mode="advanced"
                              @upload="(e) => {
                            change(input.id, (JSON.parse(e.xhr.response).data))
                          }"
                              :maxFileSize="100000000">
                    <template #empty>
                      <p>{{ $t('common.drag_files_here') }}</p>
                    </template>
                  </FileUpload>
                  <template v-else>
                    <Card style="width: 300px; overflow: hidden">
                      <template #header>
                        <Image :src="input.model" :alt="input.id" width="300" image-style="object-fit:contain"
                               height="200" preview>
                        </Image>
                      </template>
                      <template #title>
                        {{ input.label }}
                      </template>

                      <template #footer>
                        <Button icon="pi pi-arrow-right-arrow-left" class="w-100" severity="secondary"
                                @click="input.model = null" :label="$t('common.replace')"/>
                      </template>
                    </Card>

                  </template>
                </template>
              </div>
            </template>
          </div>
          <template v-if="input.bindable">
            <br>
            <Panel :header="input.label" class="w-50">
              <template #icons>
                <Button icon="pi pi-link" class="mr-2" size="small" severity="secondary" :label="$t('common.connect')"/>
                <Button icon="pi pi-plus" class="mr-2" size="small" severity="primary" @click="bindingAdd(input)" style="margin-left: .5rem"
                        :label="$t('common.add')"/>
              </template>
              <template #default>
                <Skeleton v-if="!input.bindingHost?.data || loading" width="100%" height="4rem"></Skeleton>
                <div v-else style="margin-left: -18px;margin-right: -18px">
                  <upkeep-data-table v-if="input.type === 'hasMany' && input.bindingHost?.columns"
                                     :data="input.bindingHost?.data" :columns="input.bindingHost?.columns"
                                     :model="input.slug" @needs-update="updateBinding($target, input)"></upkeep-data-table>
                </div>
              </template>
            </Panel>
          </template>
        </template>
      </div>
    </component>
  </template>
</template>
<script setup>
import {flatten} from 'flat';
import Panel from 'primevue/panel';
import LangFlag from 'vue-lang-code-flags';
import FileUpload from 'primevue/fileupload';
import {
  defineProps,
  defineModel,
  defineEmits,
  watch
} from 'vue';
import Fieldset from "primevue/fieldset";
import Select from 'primevue/select';
import InputText from 'primevue/inputtext';
import Editor from 'primevue/editor';
import Password from "primevue/password";
import Button from 'primevue/button';
import Card from 'primevue/card';
import Skeleton from 'primevue/skeleton';
import Image from 'primevue/image';
import SelectButton from 'primevue/selectbutton';
import InputNumber from 'primevue/inputnumber';
import Slider from 'primevue/slider';
import {useUpkeep} from "../../logic/UpkeepCRUD.js";
import UpkeepDataTable from "./UpkeepDataTable.vue";
const emit = defineEmits(['on-binding-add'])
const upkeep = useUpkeep();
const props = defineProps({
  formData: Array,
  modelId: String,
  loading: {
    default: false,
    type: Boolean
  }
});
const model = defineModel();
watch(model, (val) => {
  if (val) {
    let values = flatten(val);
    props.formData.forEach((el) => {
      el.children.forEach((input) => {
        if (input.bindable) {
          upkeep.binding(input.model, input.target, input.binding, route.params?.id).then((result) => {
            input.bindingHost = result
          })
        } else {
          input.model = values[input.id]
        }
      })
    });
  }
}, {immediate: true});
const change = (id, val) => {
  let path = id.split('.');
  let obj = model.value;
  for (let i = 0; i < path.length - 1; i++) {
    obj = obj[path[i]];
  }
  obj[path[path.length - 1]] = val;
}

const updateBinding = (result, input) => {
  upkeep.binding(input.model, input.target, input.binding, route.params?.id).then((result) => {
    input.bindingHost = result
  })
}

const bindingAdd = (input) => {
  emit('on-binding-add', input.target, input.binding, input.model, route.params?.id, input.through)
}
</script>
<style scoped lang="scss">
.input-panel {
  margin-bottom: 16px;

  .input-fieldset {
    display: flex;
    flex-wrap: wrap;
    gap: 24px;

    .input-element {
      display: flex;
      flex-direction: column;
      gap: 6px;

      &:not(.switch, .password, .number, .image) {
        width: calc(40%);
      }

      label {
        font-weight: bold;
        font-size: .825rem;
      }

      .input-component:not(.switch, .password, .number, .image) {
        width: 100%
      }
    }
  }
}

.w-100 {
  width: 100%;
}
</style>
